import React, { useEffect, useRef, useState } from "react";
import { Button, Container, Spinner } from "react-bootstrap";
import { FilesBlockProps } from "./files-block.props";
import WaveSurfer from "wavesurfer.js";
import { WaveSurferPlayer } from "./wave-surfer-player";
import { LazyMedia } from "./lazy-media";
import { downloadFile, FileItem, FileType, getFileType, useCachedFile } from "@/shared/lib";
import styles from "./files-block.module.css";
import { useMediaQuery } from "react-responsive";
import { TextFile } from "./text-file";

export const FilesBlock = ({
  fileUrls,
  filesArr,
  filesErr,
  msgErr,
  filesFromUser,
  loadingMessage,
  isSelected,
  handleRightClick,
  openFileModal,
  hasText,
  hasTextTop,
}: FilesBlockProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useMediaQuery({ maxWidth: 507 });
  const scrollAmount = isMobile ? 350 : 750;
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);

  const objectUrls = useRef<Record<number, string>>({});
  const waveSurferInstances = useRef<Record<number, WaveSurfer | null>>({});
  const [hovered, setHovered] = useState(false);

  const mediaFiles = filesArr.filter(file => {
    const type = getFileType(file);
    return type === "image" || type === "video";
  });


  // const mediaCount = mediaFiles.length;

  useEffect(() => {
    return () => {
      Object.values(objectUrls.current).forEach((url) =>
        URL.revokeObjectURL(url)
      );
      Object.values(waveSurferInstances.current).forEach((ws) => ws?.destroy());
    };
  }, []);

  const checkScrollButtonsVisibility = () => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    setShowLeftButton(el.scrollLeft > 0);
    setShowRightButton(el.scrollLeft + el.clientWidth < el.scrollWidth);
  };

  useEffect(() => {
    checkScrollButtonsVisibility();
    const onResize = () => checkScrollButtonsVisibility();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [fileUrls, filesArr]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      const hasHorizontalScroll = el.scrollWidth > el.clientWidth;

      if (!hasHorizontalScroll) {
        return;
      }

      e.preventDefault();
      el.scrollBy({ left: e.deltaY });
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  const onScroll = () => checkScrollButtonsVisibility();

  // const anySelected = selectedIndices.size > 0;

  const handleRightClickHandler = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    type: FileType,
    file: FileItem
  ) => {
    e.stopPropagation();
    handleRightClick(e, type, file);
  };

  const BASE_HEIGHT = 300;
  const STEP = 100;

  const getContainerHeight = (count: number) => {
    if (count <= 5) return BASE_HEIGHT;
    if (count <= 7) return BASE_HEIGHT + STEP;
    if (count <= 9) return BASE_HEIGHT + STEP * 2;
    return BASE_HEIGHT + STEP * 3;
  };

  const getGridStyle = (count: number, idx: number) => {
    switch (count) {
      case 3:
        return idx === 0
          ? { gridRow: "1 / span 2", gridColumn: "1 / 2" }
          : { gridColumn: "2 / 3" };
      case 5:
        return idx === 0
          ? { gridRow: "1 / span 2", gridColumn: "1 / 2" }
          : idx === 1
            ? { gridRow: "1 / 2", gridColumn: "2 / 3" }
            : idx === 2
              ? { gridRow: "2 / 3", gridColumn: "2 / 3" }
              : idx === 3
                ? { gridRow: "1 / 2", gridColumn: "3 / 4" }
                : { gridRow: "2 / 3", gridColumn: "3 / 4" };
      case 7:
        if (idx === 0) return { gridRow: "1 / span 2", gridColumn: "1 / 2" };
        if (idx <= 3) return { gridRow: `${idx} / ${idx + 1}`, gridColumn: "2 / 3" };
        return { gridRow: `${idx - 3} / ${idx - 2}`, gridColumn: "3 / 4" };
      case 9:
        const row = Math.floor(idx / 3) + 1;
        const col = (idx % 3) + 1;
        return { gridRow: `${row} / ${row + 1}`, gridColumn: `${col} / ${col + 1}` };
      default:
        return { gridColumn: "auto", gridRow: "auto" };
    }
  };


  return (
    <Container
      style={{
        position: "relative",
        maxWidth: "100%",
        width: "100%",
        padding: "0",
        margin: "auto",
      }}
    >
      <div
        ref={scrollRef}
        onScroll={onScroll}
        style={{
          userSelect: "none",
          overflowX: "auto",
          whiteSpace: "nowrap",
          scrollbarWidth: "none",
          padding: 0,
          position: "relative",
          // maxHeight: 250,
        }}
      >
        {loadingMessage && (
          <div
            style={{ zIndex: 1201 }}
            className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
          >
            <Spinner animation="border" size="sm" role="status" />
          </div>
        )}

        {

          mediaFiles.length > 2 ? (
            <>
              <div
                style={{
                  height: getContainerHeight(mediaFiles.length),
                  width: "100%",
                  aspectRatio: "1 / 1",
                  display: "grid",
                  gridTemplateRows: "1fr 1fr",
                  gap: "0.15rem",
                  overflow: "hidden",
          gridTemplateColumns: mediaFiles.length === 3 ? "2fr 1fr" :
            mediaFiles.length === 5 ? "2fr 1fr 1fr" :
            mediaFiles.length === 4 ? "1fr 1fr" :
              mediaFiles.length === 7 ? "2fr 1fr 1fr" :
                "1fr 1fr 1fr",
                }}
              >

                {mediaFiles.map((file, idx) => {
                  const type = getFileType(file);

                  const gridStyle = getGridStyle(mediaFiles.length, idx);

                  return (
                    <div
                      key={idx}
                      style={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        overflow: "hidden",
                        ...gridStyle,
                      }}
                    >
                      <LazyMedia
                        src={file}
                        alt={`media ${idx}`}
                        type={type}
                        isSelected={isSelected}
                        // rounded="rounded-4"
                        handleRightClick={(e) =>
                          handleRightClickHandler(e, type, file)
                        }
                        onClick={(url) => {
                          openFileModal(
                            url.startsWith("blob:") ? fileUrls[idx] : url
                          );
                        }}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  );
                })}

              </div>
            </>
          ) :
            mediaFiles.length > 1 ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "0.15rem",
                }}
              >
                {mediaFiles.map((file, idx) => {
                  const type = getFileType(file);
                  let content;

                  if (type === "image") {
                    content = (
                      <LazyMedia
                        src={file}
                        alt={`Image ${idx}`}
                        type="image"
                        isSelected={isSelected}
                        // rounded={"rounded-4"}
                        handleRightClick={(e) => handleRightClickHandler(e, type, file)}
                        onClick={(url) => {
                          if (url.startsWith("blob:")) {
                            openFileModal(fileUrls[idx]);
                          } else {
                            openFileModal(url);
                          }
                        }}
                      />
                    );
                  } else if (type === "video") {
                    content = (
                      <LazyMedia
                        src={file}
                        alt={`video ${idx}`}
                        type="video"
                        isSelected={isSelected}
                        // rounded={
                        //   hasText ? "rounded-top-5 rounded-bottom-0" :
                        //     hasTextTop ? "rounded-top-0 rounded-bottom-5" :
                        //       "rounded-5"
                        // }
                        handleRightClick={(e) => handleRightClickHandler(e, type, file)}
                        onClick={(url) => {
                          if (url.startsWith("blob:")) {
                            openFileModal(fileUrls[idx]);
                          } else {
                            openFileModal(url);
                          }
                        }}
                      />
                    );
                  }

                  return (
                    <div
                      key={idx}
                      style={{
                        position: "relative",
                        overflow: "hidden",
                        maxWidth: "100%",
                      }}
                    >
                      {content}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.45rem",
                  width: "100%",
                }}
              >
                {filesArr.map((file, idx) => {
                  const type = getFileType(file);
                  let content;

                  if (type === "image") {
                    content = (
                      <LazyMedia
                        src={file}
                        alt={`Image ${idx}`}
                        type="image"
                        isSelected={isSelected}
                        // rounded={
                        //   hasText ? "rounded-top-5 rounded-bottom-0" :
                        //     hasTextTop ? "rounded-top-0 rounded-bottom-5" :
                        //       "rounded-5"
                        // }
                        handleRightClick={(e) => handleRightClickHandler(e, type, file)}
                        onClick={(url) => {
                          if (url.startsWith("blob:")) {
                            openFileModal(fileUrls[idx]);
                          } else {
                            openFileModal(url);
                          }
                        }}
                      />
                    );
                  } else if (type === "video") {
                    content = (
                      <LazyMedia
                        src={file}
                        alt={`video ${idx}`}
                        type="video"
                        isSelected={isSelected}
                        // rounded={
                        //   hasText ? "rounded-top-5 rounded-bottom-0" :
                        //     hasTextTop ? "rounded-top-0 rounded-bottom-5" :
                        //       "rounded-5"
                        // }
                        // handleRightClick={(e) => handleRightClickHandler(e, type, file)}
                        onClick={(url) => {
                          if (url.startsWith("blob:")) {
                            openFileModal(fileUrls[idx]);
                          } else {
                            openFileModal(url);
                          }
                        }}
                      />
                    );
                  } else if (type === "audio") {
                    content = (
                      <div
                        style={{
                          width: "100%",
                          maxWidth: "300px",
                          display: "flex",
                          padding: "2px 10px 2px 4px",
                          alignItems: "center",
                          background: isSelected
                            ? "#3284ff"
                            :
                            !filesFromUser
                              ? "#6c757d"
                              : "#f8f9fa",
                        }}
                        onContextMenu={(e) => handleRightClickHandler(e, type, file)}
                      >
                        <WaveSurferPlayer src={file} isSelected={isSelected} isUser={filesFromUser} />
                      </div>
                    );
                  } else {
                    content = (
                      <TextFile
                        file={file}
                        isSelected={isSelected}
                        onClick={(url) => {
                          if (url.startsWith("blob:")) {
                            openFileModal(fileUrls[idx]);
                          } else {
                            openFileModal(url);
                          }
                        }}
                      />
                    );
                  }

                  return (
                    <span
                      key={idx}
                      style={{
                        display: "inline-block",
                        marginRight: 0,
                        position: "relative",
                        width: "100%",
                      }}
                    >
                      {content}
                    </span>
                  );
                })}
              </div>
            )}

      </div>
    </Container>
  );
};



